<template>
  <v-menu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    offset-y
    transition="slide-y-transition"
  >
    <template #activator="{ on, attrs }">
      <v-chip
        label
        small
        class="attachment-list-item"
        :close="enableDelete"
        close-icon="mdi-delete"
        v-bind="attrs"
        v-on="on"
        @click="loadPreviewImage"
        @click:close="requestFileDeletion"
      >
        <v-icon
          v-if="!isDownloading"
          left
        >
          mdi-attachment
        </v-icon>
        <v-progress-circular
          v-else
          :value="downloadPercentage"
          color="secondary"
          size="16"
          width="2"
          class="me-2"
        />
        <span
          class="mr-1 text-truncate chip-filename"
        >
          {{ filename }}
        </span>
        ({{ humanReadableFileSize }})
      </v-chip>
    </template>

    <v-card
      class="attachment-list-item-info-card"
    >
      <v-card-text
        class="pa-2 text-caption"
      >
        <div
          class="d-flex ma-2"
        >
          <v-btn
            v-if="canBeOpened"
            color="secondary"
            x-small
            dark
            depressed
            @click="openFile(false)"
          >
            <v-icon
              left
            >
              mdi-eye
            </v-icon>
            Open in new tab
          </v-btn>

          <v-spacer />

          <v-btn
            class="primary"
            x-small
            :text="isDownloading"
            dark
            depressed
            :disabled="isDownloading"
            @click="openFile(true)"
          >
            <template
              v-if="!isDownloading"
            >
              <v-icon
                left
              >
                mdi-download
              </v-icon>
              Download ({{ humanReadableFileSize }})
            </template>
            <template
              v-else
            >
              <v-progress-circular
                indeterminate
                color="white"
                size="16"
                width="2"
                class="me-2"
              />
              Prepping file... {{ downloadPercentage }}%
            </template>
          </v-btn>
        </div>

        <div
          v-if="isRepresentable"
          class="d-flex justify-center pa-1"
        >
          <div
            class="mx-auto pa-2 rounded-lg content-preview"
          >
            <v-img
              v-if="!previewError"
              contain
              width="300"
              min-height="200"
              :src="previewImage"
            >
              <template #placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <div
                    class="text-center"
                  >
                    <p>Loading preview ...</p>
                    <v-progress-circular
                      indeterminate
                      color="orange"
                    />
                  </div>
                </v-row>
              </template>
            </v-img>
            <div
              v-else
              class="grey lighten-2 py-4 rounded text--grey text-center no-preview"
            >
              There was an error getting the preview image.
            </div>
          </div>
        </div>

        <div
          v-else
          class="grey lighten-2 py-4 rounded text--grey text-center no-preview"
        >
          This file has no preview available
        </div>

        <div
          class="d-flex ma-2"
        >
          <div
            class="d-flex me-4"
          >
            <v-icon
              small
              left
            >
              mdi-paperclip
            </v-icon>
            <span>
              {{ humanReadableFileSize }}
            </span>
          </div>

          <div
            v-if="createdDate"
            class="d-flex me-4"
          >
            <v-icon
              small
              left
            >
              mdi-calendar-outline
            </v-icon>
            <span>
              {{ createdDate }}
            </span>
          </div>

          <div
            v-if="createdBy"
            class="d-flex me-4"
          >
            <v-icon
              small
              left
            >
              mdi-account-outline
            </v-icon>
            <span>
              {{ createdBy }}
            </span>
          </div>
        </div>

        <div
          class="align-start d-flex ma-2"
        >
          <v-icon
            left
            small
          >
            mdi-file-outline
          </v-icon>
          <span
            class="card-filename"
          >
            {{ filename }}
          </span>
        </div>

        <div
          v-if="documentType"
          class="align-start d-flex ma-2"
        >
          <div
            class="d-flex me-4"
          >
            <v-icon
              small
              left
            >
              mdi-file-question-outline
            </v-icon>
            <span>
              {{ documentType }}
            </span>
          </div>
        </div>

        <div
          v-if="notes"
          class="align-start d-flex ma-2"
        >
          <div
            class="d-flex me-4"
          >
            <v-icon
              small
              left
            >
              mdi-comment-text-outline
            </v-icon>
            <span>
              {{ notes }}
            </span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <NotificationDialog
      v-model="showPopupBlockedDialog"
      title="Popups Blocked"
    >
      <p>Your browser has popups blocked.</p>
      <p>Please allow popups from this site in order to open files in a new window/tab.</p>
    </NotificationDialog>
  </v-menu>
</template>

<script>
import { mapActions } from 'vuex'

import { formatSize, isMimeTypeSupportedByBrowser, openFileContentsInBrowser } from '@/utilities'
import { alertHandler } from '@/alertHandler'

import NotificationDialog from '@/components/Dialogs/Common/NotificationDialog.vue'

export default {
  name: 'AttachmentListItem',

  components: {
    NotificationDialog,
  },

  props: {
    createdBy: {
      type: String,
      default: '',
    },

    createdDate: {
      type: String,
      default: '',
    },

    documentId: {
      type: Number,
      default: undefined,
    },

    documentType: {
      type: String,
      default: '',
    },

    enableDelete: {
      type: Boolean,
      default: false,
    },

    fileId: {
      type: Number,
      required: true,
    },

    fileSize: {
      type: Number,
      required: true,
    },

    filename: {
      type: String,
      required: true,
    },

    isRepresentable: {
      type: Boolean,
      required: true,
    },

    mimeType: {
      type: String,
      required: true,
    },

    notes: {
      type: String,
      default: '',
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      downloadPercentage: 0,
      fileContents: null,
      isDownloading: false,
      isMenuOpen: false,
      previewError: false,
      previewImage: null,
      showPopupBlockedDialog: false,
    }
  },

  computed: {
    canBeOpened () {
      return !this.isDownloading && isMimeTypeSupportedByBrowser(this.mimeType)
    },

    humanReadableFileSize () {
      return formatSize(this.fileSize)
    },
  },

  methods: {
    getAttachmentData () {
      if (this.fileContents) {
        return new Promise(resolve => {
          resolve(this.fileContents)
        })
      }

      this.downloadPercentage = 0
      this.isDownloading = true

      return this.getAttachmentBlobModel({
        fileId: this.fileId,
        onDownloadProgress: progress => {
          if (progress.lengthComputable) this.downloadPercentage = Math.round((progress.loaded / progress.total) * 100)
          else if (this.fileSize) this.downloadPercentage = Math.round((progress.loaded / this.fileSize) * 100)
          else this.downloadPercentage = '??'
        },
      }).
        then(data => {
          this.fileContents = data
          return data
        }).
        catch(error => {
          this.displayAlert('error', 'There was an error downloading the attachment.')
          throw error
        }).
        finally(() => {
          this.isDownloading = false
        })
    },

    loadPreviewImage () {
      if (this.previewImage || !this.isRepresentable) return

      this.previewError = false

      this.getThumbnailForAttachment({ fileId: this.fileId }).
        then(blob => {
          const fileReader = new FileReader()
          fileReader.readAsDataURL(blob)

          fileReader.onloadend = () => {
            this.previewImage = fileReader.result
          }
        }).
        catch(() => {
          this.previewError = true
        })
    },

    openFile (forDownload) {
      this.getAttachmentData().
        then(data => {
          try {
            if (!openFileContentsInBrowser(data, this.mimeType, forDownload, this.filename)) {
              this.isMenuOpen = false
              this.showPopupBlockedDialog = true
            }
          } catch (error) {
            this.displayAlert('error', 'There was an error opening the file: ' + error)
          }
        })
    },

    requestFileDeletion () {
      this.$emit('delete-file', { documentId: this.documentId, fileId: this.fileId })
    },

    ...mapActions([
      'getAttachmentBlobModel',
      'getThumbnailForAttachment',
    ]),
  },
}
</script>

<style lang="scss" scoped>
  .attachment-list-item {
    margin: 2px;

    .chip-filename {
      max-width: 200px;
    }
  }

  .attachment-list-item-info-card {
    .card-filename {
      max-width: 294px;
      overflow-wrap: break-word;
    }

    .content-preview {
      border: 1px solid #dedede;
    }

    .no-preview {
      padding: 30px;
    }

    .v-btn--disabled {
      color: #fff !important;

      .v-icon {
        color: #fff !important;
      }
    }
  }
</style>
